import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import type { UpdateWorkItemMutation, UpdateWorkItemMutationVariables } from './__generated__/useUpdateWorkItem.gql';

const UPDATE_WORK_ITEM = gql`
  mutation UpdateWorkItem($id: ID!, $input: WorkItemUpdateInputGQL!) {
    updateWorkItem(id: $id, input: $input) {
      id
      status
      read
      # The centralized cache handler clears each linked record's workItem chip
      # on archive; the ids must ride the result for it to know which, and
      # createdAt ranks the item against a chip already on the record.
      createdAt
      transactions {
        id
      }
      bills {
        id
      }
      recurringTransaction {
        id
      }
    }
  }
`;

/**
 * Hook for archiving and unarchiving a work item — the only writes the input
 * offers; everything else on the row is assistant-authored.
 *
 * The mutation answers `null` for a row that is unexposed or invisible to the
 * caller; the centralized cache handler still drops the work-item reads on
 * that answer, since a row the server no longer acknowledges is exactly the
 * one to clear from the screen.
 *
 * Note: cache updates for this mutation are handled globally in ApolloProvider.tsx
 *
 * @returns the raw `updateWorkItem` mutation, `archiveWorkItem(id)` /
 * `unarchiveWorkItem(id)` convenience wrappers, and the Apollo mutation result
 * fields (`loading`, `error`, `data`).
 */
export const useUpdateWorkItem = () => {
  const [updateWorkItem, result] = useMutation<UpdateWorkItemMutation, UpdateWorkItemMutationVariables>(
    UPDATE_WORK_ITEM
  );

  const archiveWorkItem = React.useCallback(
    async (id: string) => {
      await updateWorkItem({ variables: { id, input: { archived: true } } });
    },
    [updateWorkItem]
  );

  const unarchiveWorkItem = React.useCallback(
    async (id: string) => {
      await updateWorkItem({ variables: { id, input: { archived: false } } });
    },
    [updateWorkItem]
  );

  return { updateWorkItem, archiveWorkItem, unarchiveWorkItem, ...result };
};
